// POS-I194: the 28 FBR scenario names exactly as the app posts and lists them.
// The source of truth is FBR_SCENARIOS in backend/src/modules/fbr/fbr-api-client.ts (DI API v1.12,
// shown in Finance > FBR > Scenario Checklist). fbr-scenario-names.spec.ts reads that file as text
// and fails when this copy drifts, so the Store Guide can never describe a different scenario
// from the one the checklist shows.

export type FbrScenarioCategory = 'standard' | 'special-rate' | 'exempt-zero' | 'sector' | 'pos-tier1';

export const FBR_SCENARIO_NAMES = {
  SN001: 'Goods at standard rate to registered buyers',
  SN002: 'Goods at standard rate to unregistered buyers',
  SN003: 'Sale of Steel (Melted and Re-Rolled)',
  SN004: 'Sale by Ship Breakers',
  SN005: 'Reduced rate sale',
  SN006: 'Exempt goods sale',
  SN007: 'Zero rated sale',
  SN008: 'Sale of 3rd schedule goods',
  SN009: 'Cotton Spinners purchase from Cotton Ginners (Textile Sector)',
  SN010: 'Telecom services rendered or provided',
  SN011: 'Toll Manufacturing sale by Steel sector',
  SN012: 'Sale of Petroleum products',
  SN013: 'Electricity Supply to Retailers',
  SN014: 'Sale of Gas to CNG stations',
  SN015: 'Sale of mobile phones',
  SN016: 'Processing / Conversion of Goods',
  SN017: 'Sale of Goods where FED is charged in ST mode',
  SN018: 'Services rendered or provided where FED is charged in ST mode',
  SN019: 'Services rendered or provided',
  SN020: 'Sale of Electric Vehicles',
  SN021: 'Sale of Cement / Concrete Block',
  SN022: 'Sale of Potassium Chlorate',
  SN023: 'Sale of CNG',
  SN024: 'Goods sold that are listed in SRO 297(1)/2023',
  SN025: 'Drugs sold at fixed ST rate under serial 81 of Eighth Schedule Table 1',
  SN026: 'Sale to End Consumer by retailers',
  SN027: 'Sale to End Consumer by retailers (3rd Schedule)',
  SN028: 'Sale to End Consumer by retailers (Reduced Rate)',
} as const;

export type FbrScenarioId = keyof typeof FBR_SCENARIO_NAMES;

export const FBR_SCENARIO_IDS = Object.keys(FBR_SCENARIO_NAMES) as FbrScenarioId[];

/**
 * The scenarios whose guide card keeps a detailed walk-through. Each was compared with the official
 * name once. Every other id shows the neutral official-name card with the three-step procedure
 * (SN013 is only a partial match: the guide described electricity supply in general, FBR's
 * scenario is supply to retailers). Add an id here only after checking its card against the
 * official name and removing any rate or legal number the app does not ship.
 */
export const FBR_DETAILED_SCENARIO_IDS: readonly FbrScenarioId[] = [
  'SN001', 'SN002', 'SN003', 'SN005', 'SN006', 'SN007', 'SN008', 'SN024', 'SN026',
];

const CATEGORY_BY_ID: Partial<Record<FbrScenarioId, FbrScenarioCategory>> = {
  SN001: 'standard',
  SN002: 'standard',
  SN005: 'special-rate',
  SN008: 'special-rate',
  SN024: 'special-rate',
  SN025: 'special-rate',
  SN006: 'exempt-zero',
  SN007: 'exempt-zero',
  SN026: 'pos-tier1',
  SN027: 'pos-tier1',
  SN028: 'pos-tier1',
};

export function scenarioCategory(id: FbrScenarioId): FbrScenarioCategory {
  return CATEGORY_BY_ID[id] ?? 'sector';
}

export function isDetailedScenario(id: string): boolean {
  return (FBR_DETAILED_SCENARIO_IDS as readonly string[]).includes(id);
}
